import {
  ActionIcon,
  Button,
  Divider,
  Grid,
  Group,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notify } from '@/lib/notify';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { IconMinus, IconPlus } from '@tabler/icons-react';
import { useEffect, useMemo } from 'react';
import {
  CONTACT_KIND_LABELS,
  CONTACT_KINDS,
  CONTACT_RELATIONSHIP_TYPES,
  type Contact,
  type ContactKind,
  type ContactRelationshipType,
} from '../data/contactTypes';
import {
  clientContactFormSchema,
  clientContactPhoneTypeValues,
  clientContactFormValuesToContactPayload,
  contactToFormValues,
  getDefaultClientContactFormValues,
  type ClientContactFormValues,
} from '@/schemas/client-contact.schema';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming',
];

export interface ClientContactFormModalProps {
  opened: boolean;
  onClose: () => void;
  /** `view` = read-only (Medical Record); `create` / `edit` = Contacts tab */
  mode: 'create' | 'edit' | 'view';
  contact: Contact | null;
  onSave?: (payload: Omit<Contact, 'id'> & { id?: string }) => void | Promise<void>;
}

export function ClientContactFormModal({
  opened,
  onClose,
  mode,
  contact,
  onSave,
}: ClientContactFormModalProps): JSX.Element {
  const isView = mode === 'view';
  const isEdit = mode === 'edit';

  /** Stable reference so Mantine always invokes the same resolver function. */
  const clientContactResolver = useMemo(() => zod4Resolver(clientContactFormSchema), []);

  const form = useForm<ClientContactFormValues>({
    initialValues: getDefaultClientContactFormValues(),
    validate: isView ? undefined : clientContactResolver,
  });

  useEffect(() => {
    if (!opened) return;
    if (isView && contact) {
      form.setValues(contactToFormValues(contact));
      return;
    }
    if (mode === 'create' || !contact) {
      form.setValues(getDefaultClientContactFormValues());
      return;
    }
    form.setValues(contactToFormValues(contact));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when modal opens / mode / contact identity changes
  }, [opened, mode, contact?.id, isView]);

  const modalTitle =
    mode === 'view' ? 'Contact details' : isEdit ? 'Edit Contact' : 'Add Contact';

  const handleSubmit = form.onSubmit(
    async (values) => {
      if (isView || !onSave) return;
      const parsed = clientContactFormSchema.safeParse(values);
      if (!parsed.success) {
        const errs: Record<string, string> = {};
        for (const issue of parsed.error.issues) {
          errs[issue.path.join('.')] = issue.message;
        }
        form.setErrors(errs);
        notify({
          type: 'error',
          title: 'Please fix the form',
          message: 'Some fields are invalid. Review the errors below.',
        });
        return;
      }
      const raw = clientContactFormValuesToContactPayload(parsed.data as ClientContactFormValues);
      const payload: Omit<Contact, 'id'> & { id?: string } = { ...raw };
      if (isEdit && contact) {
        payload.id = contact.id;
        payload.source = contact.source ?? payload.source;
        payload.relatedPersonId = contact.relatedPersonId;
        payload.contactKind = contact.contactKind ?? payload.contactKind;
      }
      await onSave(payload);
    },
    () => {
      notify({
        type: 'error',
        title: 'Please fix the form',
        message: 'Some fields are invalid. Review the errors below.',
      });
    },
  );

  const relationshipType = form.values.relationshipType as ContactRelationshipType | '';
  const contactKind = form.values.contactKind as ContactKind;

  const setContactKind = (kind: ContactKind) => {
    form.setFieldValue('contactKind', kind);
    form.setFieldValue('source', kind === 'emergency-contact' ? 'patient-contact' : 'related-person');
    form.setFieldValue('isEmergencyContact', kind === 'emergency-contact');
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={modalTitle}
      size="lg"
      centered
      styles={{
        title: { fontWeight: 600, fontSize: '1.125rem' },
        header: { borderBottom: '1px solid var(--mantine-color-gray-3)', paddingBottom: 'var(--mantine-spacing-md)' },
        body: { paddingTop: 0, paddingBottom: 0 },
      }}
    >
      <form onSubmit={handleSubmit}>
        <ScrollArea.Autosize mah="min(70vh, 36rem)" type="auto" offsetScrollbars>
        <Stack gap="md" py="md">
          <div>
            <Title order={6} mb="sm" c="dark">Contact type</Title>
            <Select
              placeholder="— select —"
              value={contactKind}
              onChange={(v) => {
                if (v) setContactKind(v as ContactKind);
              }}
              data={CONTACT_KINDS.map((k) => ({ value: k, label: CONTACT_KIND_LABELS[k] }))}
              withAsterisk={!isView}
              readOnly={isView || isEdit}
              disabled={isView || isEdit}
              description={
                isEdit
                  ? 'Contact type is fixed after create so the FHIR source stays consistent.'
                  : 'Emergency contacts are stored on the chart. Related persons and guarantors are RelatedPerson records.'
              }
            />
          </div>

          <Divider />

          <div>
            <Title order={6} mb="sm" c="dark">Name</Title>
            <Grid gutter="sm">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Legal first name"
                  placeholder="First name"
                  {...form.getInputProps('legalFirstName')}
                  withAsterisk={!isView}
                  readOnly={isView}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Middle name"
                  placeholder="Middle"
                  {...form.getInputProps('middleName')}
                  readOnly={isView}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Legal last name"
                  placeholder="Last name"
                  {...form.getInputProps('legalLastName')}
                  withAsterisk={!isView}
                  readOnly={isView}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Suffix"
                  placeholder="Jr., Sr., III"
                  {...form.getInputProps('suffix')}
                  readOnly={isView}
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <TextInput
                  label="What name do they go by?"
                  placeholder="Preferred or nickname"
                  {...form.getInputProps('preferredName')}
                  description={isView ? undefined : 'Used in place of first name in client communication (reminders, billing documents, etc.).'}
                  readOnly={isView}
                />
              </Grid.Col>
            </Grid>
          </div>

          <Divider />

          <div>
            <Title order={6} mb="sm" c="dark">Relationship</Title>
            <Select
                label="Relationship type"
                placeholder="— select —"
                value={relationshipType || null}
                onChange={(v) => form.setFieldValue('relationshipType', (v ?? '') as ClientContactFormValues['relationshipType'])}
                data={CONTACT_RELATIONSHIP_TYPES.map((t) => ({ value: t, label: t }))}
                withAsterisk={!isView}
                style={{ flex: 1, minWidth: 180 }}
                readOnly={isView}
                disabled={isView}
                error={form.errors.relationshipType}
              />
            {(relationshipType === 'Other' || relationshipType === 'Physician') && (
              <Grid gutter="sm" mt="sm">
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label="Role"
                    placeholder={relationshipType === 'Physician' ? 'e.g. Primary Care Physician' : 'e.g. Case Manager'}
                    {...form.getInputProps('role')}
                    readOnly={isView}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label="Organization"
                    placeholder="Organization name"
                    {...form.getInputProps('organization')}
                    readOnly={isView}
                  />
                </Grid.Col>
              </Grid>
            )}
          </div>

          <Divider />

          <div>
            <Title order={6} mb="xs" c="dark">Contact details</Title>
            {!isView && (
              <Text size="xs" c="gray.6" mb="sm">
                Manage contact info for reminders and notifications. An email is needed for granting Client Portal access.
              </Text>
            )}
            <Stack gap="sm">
              <div>
                <Text size="sm" fw={500} mb={4}>Email</Text>
                {form.values.emails.map((emailVal, idx) => (
                  <Group key={idx} align="flex-end" gap="sm" mb="xs" wrap="nowrap">
                    <TextInput
                      placeholder="email@example.com"
                      value={emailVal}
                      onChange={(e) => {
                        const next = [...form.values.emails];
                        next[idx] = e.currentTarget.value;
                        form.setFieldValue('emails', next);
                      }}
                      style={{ flex: 1 }}
                      readOnly={isView}
                      withAsterisk={idx === 0 && !isView && contactKind === 'related-person'}
                      required={idx === 0 && !isView && contactKind === 'related-person'}
                      error={form.errors[`emails.${idx}`]}
                    />
                    {!isView && (
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        onClick={() => form.setFieldValue(
                          'emails',
                          form.values.emails.filter((_, i) => i !== idx),
                        )}
                        disabled={form.values.emails.length <= 1}
                        aria-label="Remove email"
                      >
                        <IconMinus size={16} />
                      </ActionIcon>
                    )}
                  </Group>
                ))}
                {!isView && (
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconPlus size={14} />}
                    onClick={() => form.setFieldValue('emails', [...form.values.emails, ''])}
                    color="gold"
                    type="button"
                  >
                    Add email
                  </Button>
                )}
              </div>
              <div>
                <Text size="sm" fw={500} mb={4}>
                  Phone number {!isView ? <Text span c="red">*</Text> : null}
                </Text>
                {!isView && (
                  <Text size="xs" c="gray.6" mb="xs">
                    10–15 digits; spaces and punctuation are ignored for validation.
                  </Text>
                )}
                {form.values.phones.map((phoneEntry, idx) => (
                  <Group key={idx} align="flex-end" gap="sm" mb="xs" wrap="nowrap">
                    <TextInput
                      placeholder="(555) 000-0000"
                      value={phoneEntry.value}
                      onChange={(e) => {
                        const next = [...form.values.phones];
                        next[idx] = { ...next[idx], value: e.currentTarget.value };
                        form.setFieldValue('phones', next);
                      }}
                      style={{ flex: 1 }}
                      readOnly={isView}
                      withAsterisk={idx === 0 && !isView}
                      required={idx === 0 && !isView}
                      error={form.errors[`phones.${idx}.value`]}
                    />
                    <Select
                      data={clientContactPhoneTypeValues.map((t) => ({ value: t, label: t }))}
                      value={phoneEntry.type}
                      onChange={(v) => {
                        const next = [...form.values.phones];
                        const t = (v ?? 'Mobile') as ClientContactFormValues['phones'][number]['type'];
                        next[idx] = { ...next[idx], type: t };
                        form.setFieldValue('phones', next);
                      }}
                      allowDeselect={false}
                      w={110}
                      readOnly={isView}
                      disabled={isView}
                    />
                    {!isView && (
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        onClick={() => form.setFieldValue(
                          'phones',
                          form.values.phones.filter((_, i) => i !== idx),
                        )}
                        disabled={form.values.phones.length <= 1}
                        aria-label="Remove phone"
                        type="button"
                      >
                        <IconMinus size={16} />
                      </ActionIcon>
                    )}
                  </Group>
                ))}
                {!isView && (
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconPlus size={14} />}
                    onClick={() => form.setFieldValue('phones', [...form.values.phones, { value: '', type: 'Mobile' }])}
                    color="gold"
                    type="button"
                  >
                    Add phone
                  </Button>
                )}
              </div>
            </Stack>
          </div>

          <Divider />

          <div>
            <Title order={6} mb="sm" c="dark">Address</Title>
            <Stack gap="sm">
              <TextInput
                label="Street"
                placeholder="Search address"
                {...form.getInputProps('street')}
                readOnly={isView}
              />
              <Grid gutter="sm">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <TextInput
                    label="City"
                    placeholder="City"
                    {...form.getInputProps('city')}
                    readOnly={isView}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Select
                    label="State"
                    placeholder="Select"
                    data={US_STATES.map((s) => ({ value: s, label: s }))}
                    {...form.getInputProps('state')}
                    clearable
                    readOnly={isView}
                    disabled={isView}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <TextInput
                    label="ZIP"
                    placeholder="12345 or 12345-6789"
                    {...form.getInputProps('zip')}
                    readOnly={isView}
                  />
                </Grid.Col>
              </Grid>
            </Stack>
          </div>

          <Divider />

          <div>
            <Textarea
              label="Notes"
              placeholder="Additional notes"
              {...form.getInputProps('notes')}
              minRows={3}
              autosize
              readOnly={isView}
            />
          </div>
        </Stack>
        </ScrollArea.Autosize>

          <Group
            justify="flex-end"
            gap="sm"
            py="md"
            style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}
          >
            {isView ? (
              <Button variant="default" onClick={onClose} type="button">
                Close
              </Button>
            ) : (
              <>
                <Button variant="default" onClick={onClose} type="button">
                  Cancel
                </Button>
                <Button color="gold" type="submit">
                  {isEdit ? 'Save Changes' : 'Save'}
                </Button>
              </>
            )}
          </Group>
      </form>
    </Modal>
  );
}
